import * as _ from 'lodash';
import React, {useState, useCallback} from 'react';
import memoize from 'fast-memoize';

import {Dropdown} from 'semantic-ui-react';
import {colorN, ROBIN16, colorNRGB} from './colors';

import {backendHost} from '../config';
import {MatchParams} from '../types/base';
import {
  mediaStrings,
  MediaCardType,
  MediaCardMetadata,
  ImageMetadata,
} from '../types/media';
import {RunHistoryKeyInfo, RunHistoryRow, RunWithHistory} from '../types/run';
import {linkify} from '../util/links';
import * as runHelpers from './runhelpers';
import {MediaBrowserPanelConfig} from '../components/PanelMediaBrowser';
import AudioCard from '../components/AudioCard';
import ImageCard from '../components/ImageCard';
import PlotlyCard from '../components/PlotlyCard';
import TableCard from '../components/TableCard';
import VideoCard from '../components/VideoCard';
import HtmlCard from '../components/HtmlCard';
import Object3DCard from '../components/Object3DCard';
import BokehCard from '../components/BokehCard';
import MoleculeCard from '../components/MoleculeCard';
import {
  MediaCardProps,
  MaskControl,
  RunWithHistoryAndMediaWandb,
} from '../components/MediaCard';
import makeComp from './profiler';

export interface WBFile {
  path: string;
  _type: string;
  sha256: string;
  size: 144;
}

export const MAX_MEDIA_ROLLBACK = 500;

/**
 * Find all the keys in history that have a given `mediaType`
 *
 * History objects look like
 * [ { "loss": 1, "acc": 2},
 * { "loss": 3, "example":{"_type": "images"}}]
 *
 * We want to find all keys where "_type" is in mediaTypes.
 *
 * Returns an array of image key strings
 */

// Returns all steps from a run where the give key is present
export const stepsWithKey = (run: RunWithHistory, key: string) => {
  return (run.history && run.history.filter(step => step[key])) || [];
};
export const memoizedStepsWithKey = memoize(stepsWithKey);

// Returns all steps from a run where one of a given key is present
export const stepsWithOneOfKeys = (run: RunWithHistory, keys?: string[]) => {
  if (keys == null) {
    return [];
  }
  return (
    (run.history &&
      run.history.filter(step => _.some(keys, _.partial(_.has, step)))) ||
    []
  );
};

export function latestMedia(
  stepsWithMedia: RunHistoryRow[],
  step: number
): RunHistoryRow | undefined {
  let currentStep: RunHistoryRow | undefined;
  if (step) {
    currentStep = stepsWithMedia.find(s => s._step === step);
  }

  // if this run doesn't have media for the globally-set current step, use the last step (lower than the global step) that does have media
  if (!currentStep) {
    // Use a lazy eval to avoid search the whole list
    currentStep = _.chain(stepsWithMedia)
      .filter(m => m._step <= step)
      .last()
      .value();
  }

  return currentStep;
}

export const getMediaKeysFromKeyInfo = (keyInfo: RunHistoryKeyInfo) => {
  const keyTypes = runHelpers.keyTypes(keyInfo);
  return runHelpers.keysOfType(keyTypes, mediaStrings);
};

export const isMediaType = (key: string) => _.indexOf(mediaStrings, key) !== -1;

export const runFileSource = (matchParams: MatchParams, path: string) => {
  const {entityName, projectName, runName} = matchParams;
  const fullPath = `${backendHost()}/files/${entityName}/${projectName}/${runName}`;

  return encodeURI(`${fullPath}/${path}`);
};

// returns an encoded url for a media src file
export const mediaSrc = (
  matchParams: MatchParams,
  fileParams: string[], // [mediaKey, stepIndex] for images or [mediaKey, stepIndex, mediaIndex] for audio
  mediaType: 'images' | 'audio' | 'html' | 'videos',
  extension?: string
) => {
  const {entityName, projectName, runName} = matchParams;
  const fullPath = `${backendHost()}/files/${entityName}/${projectName}/${runName}`;
  const fileName = mediaFilename(fileParams, mediaType, extension);
  return encodeURI(`${fullPath}/${fileName}`);
};

type MediaType = 'images' | 'audio' | 'html' | 'object3D' | 'videos';

// returns an encoded filename for a media src file (includes the 'media/${mediaType}/' prefix)
export const mediaFilename = (
  fileParams: string[],
  mediaType: MediaType,
  extension?: string
) => {
  const filename = fileParams.join('_');
  let fileType: string;
  if (extension) {
    fileType = extension;
  } else if (mediaType === 'images') {
    fileType = 'jpg';
  } else if (mediaType === 'audio') {
    fileType = 'wav';
  } else if (mediaType === 'videos') {
    fileType = 'gif';
  } else {
    fileType = 'html';
  } // we currently only support images, audio, and html
  return `media/${mediaType}/${filename}.${fileType}`;
};

export const filePath = (filename: string, mediaType: MediaType) => {
  return encodeURI(`media/${mediaType}/${filename}`);
};

/* === mediaType: IMAGES === */

/* === AUDIO === */

// returns the 'durations' array for the given run/step/key
export const getAudioDurations = (
  run: any,
  stepIndex: number,
  mediaKey: string
) => {
  return [];
};

// returns the max audio duration for the given parameters.
// note: this behaves differently if allRuns.length === 1 (e.g. on run page)
// if we have one run, we want to compare durations across all mediaIndexes for that run+step+key
// but if we have multiple runs (allRuns.length > 1, e.g. on report page), we want to compare durations for a single mediaIndex across runs
export const getMaxAudioDuration = (
  allRuns: RunWithHistory[],
  stepIndex: number,
  mediaKey: string,
  mediaIndex: number
): number =>
  _.max(
    allRuns.map(r => {
      const allDurations = getAudioDurations(r, stepIndex, mediaKey);
      return allRuns.length === 1
        ? _.max(allDurations) // run page
        : allDurations[mediaIndex]; // report page
    })
  ) || 0;

// returns the width of the current waveform in percent (not px!)
// calculated by comparing its duration vs the duration of all currently-displayed waveforms
export const scaledWaveformWidth = (
  allRuns: RunWithHistory[],
  runIndex: number,
  stepIndex: number,
  mediaKey: string,
  mediaIndex: number
) => {
  const maxAudioDuration: number = getMaxAudioDuration(
    allRuns,
    stepIndex,
    mediaKey,
    mediaIndex
  );
  const currentAudioDuration = getAudioDurations(
    allRuns[runIndex],
    stepIndex,
    mediaKey
  )[mediaIndex];

  return 100 * ((currentAudioDuration || 0) / maxAudioDuration);
};

// This is a rollback mechanism for getting data from previous step when there
// is issues with the currrent step.
//
// This function is provided to centralize that logic between media
export const useStepWithRollback = (props: MediaCardProps) => {
  const [currentStep, setCurrentStep] = useState<number | undefined>();
  const [baseStep, setBaseStep] = useState<number>();
  const [rollbackCount, setRollbackCount] = useState(0);

  const stepsWithMedia = memoizedStepsWithKey(props.run, props.mediaKey);

  // We want to set a base step and a current step
  // - current step can be rolled back locally
  // - base step is controlled by the parent
  // When base step is changed by the parent we need to reset the rollback state
  if (props.globalStep !== baseStep) {
    const latestStepData = latestMedia(stepsWithMedia, props.globalStep);
    setBaseStep(props.globalStep);
    setRollbackCount(0);
    if (latestStepData != null) {
      setCurrentStep(latestStepData._step);
    }
  }

  const fallbackMedia = latestMedia(
    stepsWithMedia,
    currentStep ? Math.max(currentStep - 1, 0) : 0
  );
  const fallbackStep = fallbackMedia && fallbackMedia._step;

  const [failedToLoad, setFailedToLoad] = useState(false);

  const rollback = useCallback(() => {
    if (fallbackStep !== currentStep && rollbackCount < MAX_MEDIA_ROLLBACK) {
      setCurrentStep(fallbackStep);
      setRollbackCount(rollbackCount + 1);
    } else {
      setFailedToLoad(true);
    }
  }, [currentStep, fallbackStep, rollbackCount]);

  const currentStepData = stepsWithMedia.find(v => v._step === currentStep);

  return {
    currentStep,
    baseStep,
    rollback,
    rollbackCount,
    stepData: currentStepData,
    failedToLoad,
  };
};

// Helper components for media cards

export const MediaStepDropdown = makeComp(
  ({
    step,
    globalStep,
    stepDropdownOptions,
    setStep,
  }: {
    step: number | undefined;
    globalStep: number;
    stepDropdownOptions: any;
    setStep: (n: number) => void;
  }) => {
    if (_.isEmpty(stepDropdownOptions)) {
      return null;
    }

    const stepMismatch =
      !_.isUndefined(globalStep) && step !== globalStep ? 'step-mismatch' : '';

    return (
      <Dropdown
        key={step || 'none'}
        floating
        lazyLoad
        className={'panel-media-step ' + stepMismatch}
        scrolling
        compact
        value={step}
        options={stepDropdownOptions}
        onChange={(e, {value}) => {
          setStep(value as number);
        }}
      />
    );
  },
  {id: 'MediaStepDropdown', memo: true}
);

export const makeCaptions = (
  currentMediaMetadata: {
    caption?: any;
    captions?: any[];
  },
  mediaIndex: number,
  mediaGroupingCount?: number
) => {
  if (!currentMediaMetadata) {
    return [];
  }
  const groupCount = mediaGroupingCount ?? 1;

  // Some captions are at the media file level
  let captions: string[];
  if ('caption' in currentMediaMetadata && currentMediaMetadata.caption) {
    captions = [String(currentMediaMetadata.caption)];
  } else if (
    'captions' in currentMediaMetadata &&
    currentMediaMetadata.captions
  ) {
    // Some captions are a combination of a sequence of images
    // from cli/data_types: seq_to_json
    captions = currentMediaMetadata.captions
      .slice(mediaIndex * groupCount, (mediaIndex + 1) * groupCount)
      .map(c => String(c));
  } else {
    captions = [];
  }

  const renderedCaptions = captions.map((caption, i) => {
    return (
      <div key={i} style={{flexGrow: 1}}>
        <div className="image-card-caption-text">
          {caption.split('\n').map((cLine, j) => {
            let c: string | ReturnType<typeof linkify> = cLine;
            if (!_.isEmpty(c)) {
              c = linkify(c, {onClick: e => e.stopPropagation()});
            }
            return <div key={j}>{c}</div>;
          })}
        </div>
      </div>
    );
  });

  return renderedCaptions;
};

export const labelComponent = (
  props: MediaCardProps,
  currentStep: number | undefined,
  titleLink: JSX.Element
) => {
  return (
    props.labels &&
    props.labels.map(l => {
      return (
        <div key={l} style={{fontSize: '10px', lineHeight: '16px'}}>
          {l === 'step' ? (
            <span>Step {currentStep}</span>
          ) : l === 'index' ? (
            <span>Index {props.mediaIndex}</span>
          ) : l === 'run' ? (
            <span className="text__single-line" style={{display: 'block'}}>
              {props.disableRunLink ? props.run.displayName : titleLink}
            </span>
          ) : (
            'None'
          )}
        </div>
      );
    })
  );
};

// ** Migrations **

type DeprecatedConfigValues = DeprecatedZoom & DeprecatedLayoutConfig;
export const runMediaPanelMigrations = (
  config: MediaBrowserPanelConfig & DeprecatedConfigValues
): MediaBrowserPanelConfig => {
  return convertMediaPanelConfigToLayoutV2(
    convertMediaPanelConfigZoomToGallery(config)
  );
};
//
// This migration uses heuristics to set old views into a close approximation of their old image sizes,
// but in their new gallery layout
//
// Zoom to Columns Migration
interface DeprecatedZoom {
  zoom: number;
}

// Use a negative one to indicate the zoom migration
// has been run and this is a stale key
export const ZOOM_MIGRATED_TOKEN = 'ZOOM_MIGRATED';
export const convertMediaPanelConfigZoomToGallery = (
  config: MediaBrowserPanelConfig & DeprecatedZoom
): MediaBrowserPanelConfig => {
  const newConfig = _.clone(config);

  if (config.zoom != null && (config as any).zoom !== ZOOM_MIGRATED_TOKEN) {
    const zoom = config.zoom;

    // Migrate all non zoom panels to a simple 3 column actualSize
    if (zoom === 1) {
      newConfig.actualSize = true;
      newConfig.columnCount = 3;
      // Migrate any other zooms to a best guess of their columnCount
    } else if (zoom > 2.5) {
      newConfig.columnCount = 1;
    } else if (zoom <= 2.5) {
      newConfig.columnCount = 2;
    } else if (zoom < 1.4) {
      newConfig.columnCount = 3;
    } else if (zoom < 0.3) {
      newConfig.columnCount = 5;
    } else if (zoom < 0.25) {
      newConfig.columnCount = 8;
    }
    (newConfig as any).zoom = ZOOM_MIGRATED_TOKEN;
  }

  return newConfig;
};

export const getFirstMediaMetadata = (
  history: RunHistoryRow[],
  key: string,
  currentStep: number
): MediaCardMetadata | null => {
  // first, try to find the current step
  let firstHistoryStep = _.find(
    history,
    hr => hr._step === currentStep && key in hr
  );

  // if we were unable to find the current step or the current step does
  // not contain the desired key, take the first step with the desired key
  if (firstHistoryStep == null) {
    firstHistoryStep = _.find(history, hr => key in hr);
  }

  return firstHistoryStep && firstHistoryStep[key];
};

export const getMasks = (
  mediaMetadata: ImageMetadata,
  mediaIndex: number
): {
  [key: string]: WBFile;
} | null => {
  if (mediaMetadata.masks) {
    return mediaMetadata.masks;
  } else if (mediaMetadata.all_masks) {
    return mediaMetadata.all_masks[mediaIndex];
  } else {
    return {};
  }
};

export const getBoundingBoxes = (
  mediaMetadata: ImageMetadata,
  mediaIndex: number
): {
  [key: string]: WBFile;
} => {
  if (mediaMetadata.boxes != null) {
    return mediaMetadata.boxes;
  } else if (
    mediaMetadata.all_boxes != null &&
    mediaMetadata.all_boxes[mediaIndex] != null
  ) {
    const v = mediaMetadata.all_boxes[mediaIndex];
    // The original box API was un-nested and returned path at the top level
    // only a few runs were logged by wandb test users so we don't want to support
    // this anymore.
    // Return an empty object to ignore these boxes and pretend as if there is no boxes
    if ((v as any).path) {
      return {};
    }
    return v;
  } else {
    return {};
  }
};

// Layout V2 Migration:
//
// Introduces a new mode, gallery mode and grid mode
// along with allowing multiple media keys.

// The old layout has a set of image keys it used
// This will both be rolled into a new array
export interface DeprecatedLayoutConfig {
  mediaKey?: string; // key for media, like 'examples'
  imageKey?: string; // legacy key for images, like 'examples.'  renamed to mediaKey.
}
export const convertMediaPanelConfigToLayoutV2 = (
  config: MediaBrowserPanelConfig & DeprecatedLayoutConfig
): MediaBrowserPanelConfig => {
  const oldKey = config.mediaKey ?? config.imageKey;
  if (oldKey && config.mediaKeys == null) {
    const newConfig = _.clone(config);
    newConfig.mediaKeys = [oldKey];

    return newConfig;
  }

  return config;
};

const mediaToComponentMap: {
  [k in MediaCardType]:
    | React.FunctionComponent<MediaCardProps>
    // These need special types because they are not function components yet
    | typeof TableCard
    | typeof PlotlyCard
    | typeof MoleculeCard;
} = {
  audio: AudioCard,
  image: ImageCard,
  video: VideoCard,
  table: TableCard,
  plotly: PlotlyCard,
  html: HtmlCard,
  object3D: Object3DCard,
  bokeh: BokehCard,
  molecule: MoleculeCard,
};

export const mediaTypeToComponent = (k: MediaCardType) => {
  return mediaToComponentMap[k];
};

export const WANDB_MASK_CLASS_LABEL_KEY = 'mask/class_labels';
export const WANDB_BBOX_CLASS_LABEL_KEY = 'bounding_box/class_labels';
export const WANDB_DELIMETER = '_wandb_delimeter_';

export const getSingletonValue = (
  run: {
    _wandb: string;
  },
  type: string,
  key: string
) => {
  return _.get(run._wandb, [type, key, 'value']);
};

export interface ClassLabels {
  [key: number]: string;
}

export interface ClassLabelNode {
  key: string;
  type: string;
  value: ClassLabels;
}

// Gets all class labels from a set of runs
// that are attached to any of the given
// mediaKeys of the WANDB_KEY type
//
// Returns the class labels into a merged object
// with a hierarchy of {mediaKey: {subKey: classLabels}}
export function classLabelMap(
  runs: RunWithHistoryAndMediaWandb[],
  mediaKeys: string[],
  WANDB_KEY: string
): {
  [media: string]: {
    [typeKey: string]: ClassLabelNode;
  };
} {
  return runs
    .map(r => {
      const labelMap = r._wandb[WANDB_KEY] ?? {};
      return Object.keys(labelMap)
        .map(k => {
          const [mediaKey, subKey] = k.split(WANDB_DELIMETER);
          if (_.includes(mediaKeys, mediaKey)) {
            const v = labelMap[k];
            return {subKey, mediaKey, value: v};
          } else {
            return null;
          }
        })
        .filter(v => v != null) as Array<{
        subKey: string;
        mediaKey: string;
        value: ClassLabelNode;
      }>;
    })
    .reduce((acc, val) => acc.concat(val), [])
    .reduce(
      (acc, v) => {
        if (acc[v.mediaKey] == null) {
          acc[v.mediaKey] = {};
        }
        acc[v.mediaKey][v.subKey] = v.value;

        return acc;
      },
      {} as {
        [mediaKey: string]: {
          [maskKey: string]: ClassLabelNode;
        };
      }
    );
}

// Right now this is just a wrapper, but
// use this as a single code path for segmentation colors
// for future flexibiltiy
export const segmentationMaskColor = (id: number) => {
  return colorNRGB(id, ROBIN16);
};

// Right now this is just a wrapper, but
// use this as a single code path for segmentation colors
// for future flexibiltiy
export const boxColor = (id: number) => {
  return colorN(id, ROBIN16);
};

export const DEFAULT_ALL_MASK_CONTROL = {
  disabled: false,
  opacity: 0.6,
} as MaskControl;

export const DEFAULT_CLASS_MASK_CONTROL = {
  disabled: false,
  opacity: 1,
} as MaskControl;
